// Jamora Assist (remote assistance): labels and pure helpers shared by the Assistance page, the session page and
// the navigation badge. The state of a session always comes from the server (which reads Jamora): nothing here
// decides a transition, it only describes what was received.
import { ASSIST_READ, hasPermission } from "./permissions";

export const QUEUE_POLL_MS = 10000;
export const SESSION_POLL_MS = 5000;
export const NAV_BADGE_POLL_MS = 20000;

export const STATUS_LABELS = {
  invited: "Invitation envoyée",
  pending: "En attente",
  accepted: "Prise en charge",
  connecting: "Connexion en cours",
  active: "En cours",
  ended: "Terminée",
  expired: "Expirée",
  rejected: "Annulée",
};

export const STATUS_CLASS = {
  invited: "bg-violet-50 text-violet-700",
  pending: "bg-amber-50 text-amber-700",
  accepted: "bg-sky-50 text-sky-700",
  connecting: "bg-sky-50 text-sky-700",
  active: "bg-emerald-50 text-emerald-700",
  ended: "bg-slate-100 text-slate-600",
  expired: "bg-slate-100 text-slate-600",
  rejected: "bg-slate-100 text-slate-600",
};

const OPEN = ["accepted", "connecting", "active"];
const FINISHED = ["ended", "expired", "rejected"];

// An assistance proposed from the CRM to a pharmacy account (contract 0.6.0) waits for that person's answer.
export const isInvited = (status) => status === "invited";

export const isOpen = (status) => OPEN.includes(status);
export const isFinished = (status) => FINISHED.includes(status);

// Who or what ended a session, in the technician's words (contract: endedBy).
const ENDED_BY_TEXT = {
  pharmacien: "par le pharmacien",
  technicien: "par le technicien",
  "system-timeout": "par le système : la connexion a été perdue trop longtemps",
  "system-disabled": "par le système : l'assistance a été désactivée pour cette pharmacie",
  "system-operator-change": "par le système : l'opérateur qui a fait la demande n'est plus connecté au poste",
  "system-unanswered": "par le système : personne n'a répondu à la proposition",
};

export function endedSummary(item) {
  if (!item || !isFinished(item.status)) return null;
  if (item.origin === "technician" && item.status === "rejected") {
    return item.ended_by === "pharmacien" ? "Proposition refusée par la personne contactée" : "Proposition annulée par le technicien";
  }
  if (item.origin === "technician" && item.ended_by === "system-unanswered") return "Proposition restée sans réponse";
  if (item.status === "rejected") return "Demande annulée par le pharmacien";
  // Requests nobody took, or sessions that never started, expire on their own.
  if (item.ended_by === "system-unclaimed") {
    return item.accepted_at ? "Expirée : la session n'a pas démarré à temps" : "Expirée : aucun technicien ne l'a prise en charge à temps";
  }
  const label = item.status === "expired" ? "Expirée" : "Terminée";
  const by = ENDED_BY_TEXT[item.ended_by];
  return by ? `${label} ${by}` : label;
}

// Timeline of one assistance (contract audit trail), in the technician's words.
export const TIMELINE_LABELS = {
  invited: "Assistance proposée par le technicien",
  invitation_cancelled: "Proposition annulée par le technicien",
  invitation_declined: "Proposition refusée par la personne contactée",
  created: "Demande envoyée par la pharmacie",
  accepted: "Prise en charge par le technicien",
  connecting: "Connexion à la salle",
  connected: "Participant connecté",
  capacity_wait: "En attente d'une ligne libre",
  screen_share_started: "Partage d'écran démarré",
  screen_share_stopped: "Partage d'écran arrêté",
  disconnected: "Participant déconnecté",
  reconnected: "Participant reconnecté",
  ended: "Assistance terminée",
  expired: "Demande expirée",
  rejected: "Demande annulée",
  co_technician_joined: "Technicien ajouté à l'assistance",
  co_technician_left: "Technicien sorti de l'assistance",
};

// Who connected / disconnected: the server translates Jamora's actor into a role (never an id). Without it (an actor the CRM
// does not recognise), the neutral wording stays.
const PRESENCE_EVENTS = {
  connected: ["Pharmacien connecté", "Technicien connecté"],
  disconnected: ["Pharmacien déconnecté", "Technicien déconnecté"],
  reconnected: ["Pharmacien reconnecté", "Technicien reconnecté"],
};

export function timelineLabel(entry) {
  if (entry.who?.role === "technician" && entry.event === "co_technician_joined") return `${entry.who.name} a rejoint l'assistance`;
  if (entry.who?.role === "technician" && entry.event === "co_technician_left") return `${entry.who.name} a quitté l'assistance`;
  const presence = PRESENCE_EVENTS[entry.event];
  if (presence && entry.who?.role === "pharmacist") return presence[0];
  if (presence && entry.who?.role === "technician") return `${presence[1]} — ${entry.who.name}`;
  return TIMELINE_LABELS[entry.event] || entry.event;
}

// Real duration: from both participants being there (`started_at`, contract 0.4.2) to the end. A Jamora on an older
// contract has no `started_at`: the acceptance is the closest known start.
export function sessionDuration(item) {
  return duration(item?.started_at || item?.accepted_at, item?.ended_at);
}

export function canUseAssist(user) {
  return !!user?.features?.assist && hasPermission(user, "support:handle");
}

// Proposing an assistance to a pharmacy account (contract 0.6.0): `support:initiate` AND `support:handle`.
export function canInitiateAssist(user) {
  return canUseAssist(user) && hasPermission(user, "support:initiate");
}

// Opening the Assistance page at all: technicians, and supervisors who only read the history and the reports.
export function canOpenAssist(user) {
  return !!user?.features?.assist && hasPermission(user, ASSIST_READ);
}

// The backend answers {detail: {code, message}} for every Assist error; anything else (network, 5xx from a proxy)
// falls back to a generic, actionable message.
export function assistError(error, fallback = "Service d'assistance indisponible. Réessayez dans quelques instants.") {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return { code: detail.code || null, status: error.response.status, message: detail.message };
  }
  return { code: null, status: error?.response?.status || null, message: fallback };
}

function minutesBetween(fromIso, to) {
  const from = Date.parse(fromIso);
  if (Number.isNaN(from)) return null;
  return Math.max(0, Math.floor((to - from) / 60000));
}

function hoursMinutes(minutes) {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${String(m).padStart(2, "0")}` : `${h} h`;
}

export function waitingSince(iso, now = Date.now()) {
  const minutes = minutesBetween(iso, now);
  if (minutes === null) return "Inconnu";
  if (minutes < 1) return "à l'instant";
  return `il y a ${hoursMinutes(minutes)}`;
}

export function duration(startIso, endIso) {
  if (!startIso || !endIso) return "Inconnue";
  const minutes = minutesBetween(startIso, Date.parse(endIso));
  if (minutes === null) return "Inconnue";
  return minutes < 1 ? "moins d'1 min" : hoursMinutes(minutes);
}

// Timeline entries: several events often fall within the same minute.
export function formatTimeSeconds(iso) {
  if (!iso) return "Inconnue";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Inconnue";
  return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function formatDateTime(iso) {
  if (!iso) return "Inconnue";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Inconnue";
  return date.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export const assistSessionPath = (id) => `/assistance/${id}`;

// The queue changed on this page (a request was taken or vanished): the navigation badge refreshes now rather
// than at its next polling tick.
export const QUEUE_CHANGED_EVENT = "jamora:assist-queue-changed";
export const notifyQueueChanged = () => window.dispatchEvent(new Event(QUEUE_CHANGED_EVENT));

// Several technicians in one assistance (Paramètres › Assistance; Jamora's hard cap is 5, principal included).
export const MULTI_TECHNICIAN_MAX = 5;

// Who is in the assistance right now: the principal first, then the co-technicians still there.
export function presentTechnicians(item) {
  if (!item?.technician) return [];
  const others = (item.co_technicians || []).filter((c) => !c.left_at).map((c) => ({ id: c.id, name: c.name, principal: false }));
  return [{ id: item.technician.id, name: item.technician.name, principal: true }, ...others];
}

// Everyone who took part, for the history's « Technicien » column: the principal first, then each co-technician once.
export function historyTechnicians(item) {
  const names = item?.technician?.name ? [item.technician.name] : [];
  (item?.co_technicians || []).forEach((c) => { if (c.name && !names.includes(c.name)) names.push(c.name); });
  return names;
}

// « avec Awa Ndiaye et Ibou Fall »: everyone who joined at some point (each name once), for the history and the detail.
// With `selfId` (one's own history), it names the OTHER people of the session — principal included — never oneself.
export function coTechniciansText(item, selfId) {
  const people = selfId ? [item?.technician, ...(item?.co_technicians || [])] : (item?.co_technicians || []);
  const names = [...new Set(people.filter((c) => c?.name && (!selfId || c.id !== selfId)).map((c) => c.name))];
  if (!names.length) return "";
  return `avec ${names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} et ${names[names.length - 1]}`}`;
}

// « vu il y a 3 min » — the last time the person's application was open: a hint to pick someone, never a guarantee.
export function lastSeenText(iso, now = Date.now()) {
  if (!iso) return "jamais vu";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "jamais vu";
  const minutes = Math.max(0, Math.floor((now - then) / 60000));
  if (minutes < 1) return "vu à l'instant";
  if (minutes < 60) return `vu il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vu il y a ${hours} h`;
  return `vu il y a ${Math.floor(hours / 24)} j`;
}
